import React from "react";
import { Helmet } from "react-helmet-async";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { Music, Star, Calendar, Trophy } from "lucide-react";
import { Link } from 'react-router-dom';

const HERO_IMAGE_URL = "https://media.hivetivity.com/young-trainer-helping-trainees-practicing-gymnastics.jpg";

const DanceStudios = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Dance Studio Management Software - Hivetivity</title>
        <meta name="description" content="Streamline your dance studio operations with Hivetivity. Manage classes, students, scheduling, and payments all in one place." />
      </Helmet>

      <TopNavbar />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-purple-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#5a5a59] mb-6">
                  Transform Your Dance Studio Management
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Streamline your operations, inspire young dancers, and watch your studio flourish with our all-in-one management platform.
                </p>
                <Link to="/business-trial">
                  <Button className="bg-[#FA8C00] hover:bg-[#FA8C00]/90 text-white px-8 py-6 text-lg">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <img
                  src={HERO_IMAGE_URL}
                  alt="Young trainer helping trainees practicing gymnastics"
                  width={600}
                  height={400}
                  className="rounded-2xl shadow-xl"
                  style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#5a5a59] mb-12">
              Everything Your Dance Studio Needs
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="p-6 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                <Music className="w-12 h-12 text-[#FA8C00] mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Class Management</h3>
                <p className="text-gray-600">Easily organize classes, track attendance, and manage your dance curriculum.</p>
              </div>
              <div className="p-6 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                <Star className="w-12 h-12 text-[#FA8C00] mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Student Progress</h3>
                <p className="text-gray-600">Monitor student development and celebrate their dance journey.</p>
              </div>
              <div className="p-6 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                <Calendar className="w-12 h-12 text-[#FA8C00] mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Smart Scheduling</h3>
                <p className="text-gray-600">Effortlessly manage class schedules, instructors, and studio space.</p>
              </div>
              <div className="p-6 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                <Trophy className="w-12 h-12 text-[#FA8C00] mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Recital Planning</h3>
                <p className="text-gray-600">Streamline recital organization, costumes, and performances.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20 bg-purple-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-[#5a5a59] mb-8">
                Loved by Dance Studio Owners
              </h2>
              <blockquote className="text-xl text-gray-600 italic mb-6">
                "Hivetivity has transformed how I run my dance studio. From class scheduling to student progress tracking, everything is now seamlessly organized in one place."
              </blockquote>
              <p className="font-semibold text-[#5a5a59]">
                - Sarah Johnson, Dance Dreams Academy
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-[#5a5a59] mb-6">
              Ready to Transform Your Dance Studio?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join hundreds of successful dance studios using Hivetivity to streamline their operations and focus on what matters most - inspiring young dancers.
            </p>
            <Link to="/business-trial">
              <Button className="bg-[#FA8C00] hover:bg-[#FA8C00]/90 text-white px-8 py-6 text-lg">
                Start Your Free Trial Today
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DanceStudios;
